import React, { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Building2, Users, Target, GraduationCap,
  Download, Award, Search
} from 'lucide-react';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/exportUtils';

// ─── Individual College Card ──────────────────────────────────────────────────
const CollegeCard = ({ college, isDark, index }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchedOnce, setFetchedOnce] = useState(false);

  const handleToggle = useCallback(async () => {
    const willOpen = !open;
    setOpen(willOpen);

    if (willOpen && !fetchedOnce) {
      setLoading(true);
      setFetchedOnce(true);
      try {
        const res = await api.get(`/analytics/college/${college._id}`);
        if (res.data.success) setData(res.data.data);
      } catch (e) {
        console.error('College card analytics error:', e);
      } finally {
        setLoading(false);
      }
    }
  }, [open, fetchedOnce, college._id]);

  const depts = useMemo(() => data?.students?.byDepartment || [], [data]);
  const placementScore = useMemo(() => {
    if (!data) return 0;
    const comp = data.kpis?.courseCompletion || 0;
    const assess = data.assessments?.avgScore || 0;
    const engageRate = data.students?.total > 0
      ? (data.students.active / data.students.total) * 100 : 0;
    return Math.round(comp * 0.4 + assess * 0.3 + engageRate * 0.3);
  }, [data]);

  const maxDeptStudents = useMemo(() =>
    depts.length > 0 ? Math.max(...depts.map(d => d.value || 0), 1) : 1,
    [depts]);


  const handleExportCSV = () => {
    if (!depts.length) return;
    const rows = depts.map(d => ({
      'College': college.collegeName || college.institutionName,
      'Department': d.name,
      'Students': d.value,
      'Assessment Avg (%)': Math.round(data?.assessments?.avgScore || 0),
      'Course Completion (%)': Math.round(data?.kpis?.courseCompletion || 0),
      'Placement Score (%)': placementScore,
      'Attendance': 'N/A — Requires backend support',
      'Participation': 'N/A — Requires backend support',
    }));
    exportToCSV(
      rows,
      Object.keys(rows[0]).map(k => ({ header: k, accessor: k })),
      `${(college.collegeName || college.institutionName || 'College').replace(/\s+/g, '_')}_Departments`
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className={`rounded-2xl border overflow-hidden transition-shadow duration-300 ${
        isDark
          ? `border-[#1a3884]/50 bg-[#002147] ${open ? 'shadow-lg shadow-[#000]/20' : ''}`
          : `border-gray-200 bg-white ${open ? 'shadow-md' : 'shadow-sm'}`
      }`}
    >
      {/* ── Card Header (always visible) ── */}
      <button
        onClick={handleToggle}
        className={`w-full flex items-center justify-between p-5 text-left transition-colors ${
          isDark ? 'hover:bg-[#0F225C]/30' : 'hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#1A3884]/40' : 'bg-blue-50'}`}>
            <Building2 size={20} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-bold text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {college.collegeName || college.institutionName || '—'}
            </p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {college.city && (
                <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{college.city}</span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                college.status === 'active' || !college.status
                  ? isDark ? 'bg-emerald-950/40 text-emerald-400' : 'bg-emerald-50 text-emerald-700'
                  : isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
              }`}>
                {college.status || 'active'}
              </span>
            </div>
          </div>
          {/* Stats chips */}
          <div className="flex items-center gap-5 flex-shrink-0 mr-2 sm:mr-4">
            <div className="text-right">
              <p className={`text-xl font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {(college.totalStudents || 0).toLocaleString()}
              </p>
              <p className={`text-[9px] font-bold uppercase tracking-wide mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Students</p>
            </div>
            <div className="text-right hidden sm:block">
              <p className={`text-xl font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {college.departments?.length || college.totalDepartments || 0}
              </p>
              <p className={`text-[9px] font-bold uppercase tracking-wide mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Depts</p>
            </div>
          </div>
        </div>
        <ChevronDown
          size={15}
          className={`transition-transform duration-300 flex-shrink-0 ${open ? 'rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
        />
      </button>

      {/* ── Expanded Section ── */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="expanded"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className={`border-t px-5 pb-6 pt-4 ${isDark ? 'border-[#1a3884]/30' : 'border-gray-100'}`}>
              {loading ? (
                <div className="flex items-center justify-center h-24">
                  <div className="relative w-10 h-10">
                    <div className={`absolute inset-0 border-4 rounded-full ${isDark ? 'border-[#1a3884]/40' : 'border-gray-200'}`} />
                    <div className="absolute inset-0 border-4 border-[#284BB3] rounded-full border-t-transparent animate-spin" />
                  </div>
                </div>
              ) : data ? (
                <>
                  {/* KPI strip */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                    {[
                      { label: 'Active Students', value: data.students?.active || 0, icon: Users, color: '#10B981' },
                      { label: 'Avg Assessment', value: `${Math.round(data.assessments?.avgScore || 0)}%`, icon: Target, color: '#3B82F6' },
                      { label: 'Course Completion', value: `${Math.round(data.kpis?.courseCompletion || 0)}%`, icon: GraduationCap, color: '#F59E0B' },
                      { label: 'Placement Score', value: `${placementScore}%`, icon: Award, color: '#8B5CF6' },
                    ].map(item => (
                      <div key={item.label} className={`rounded-xl p-3 border ${isDark ? 'border-[#1a3884]/30 bg-[#0F225C]/20' : 'border-gray-200 bg-gray-50'}`}>
                        <p className="text-xl font-black" style={{ color: item.color }}>{item.value}</p>
                        <p className={`text-[10px] font-semibold mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Department breakdown table */}
                  {depts.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <p className={`text-xs font-bold uppercase tracking-wide ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                          Department Breakdown
                        </p>
                        <button
                          onClick={handleExportCSV}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                            isDark ? 'bg-[#1A3884]/30 text-blue-400 hover:bg-[#1A3884]/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                          }`}
                        >
                          <Download size={11} /> Export CSV
                        </button>
                      </div>
                      <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-[#1a3884]/30' : 'border-gray-200'}`}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className={isDark ? 'bg-[#0F225C]/40' : 'bg-gray-50'}>
                              {['Department', 'Students', 'Assessment', 'Placement', 'Attendance', 'Participation'].map(h => (
                                <th key={h} className={`px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wide ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${isDark ? 'divide-[#1a3884]/20' : 'divide-gray-100'}`}>
                            {depts.map(dept => (
                              <tr key={dept.name} className={`transition-colors ${isDark ? 'hover:bg-[#0F225C]/30' : 'hover:bg-gray-50'}`}>
                                <td className={`px-4 py-2.5 font-semibold text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>{dept.name}</td>
                                <td className="px-4 py-2.5 text-xs">
                                  <div className="flex items-center gap-2">
                                    <div className={`flex-1 h-1.5 rounded-full ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                      <div
                                        className="h-full rounded-full"
                                        style={{ width: `${(dept.value / maxDeptStudents) * 100}%`, background: '#3B82F6' }}
                                      />
                                    </div>
                                    <span className={`font-bold w-8 text-right flex-shrink-0 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{dept.value}</span>
                                  </div>
                                </td>
                                <td className={`px-4 py-2.5 text-xs font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                  {Math.round(data.assessments?.avgScore || 0)}%
                                </td>
                                <td className={`px-4 py-2.5 text-xs font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                                  {placementScore}%
                                </td>
                                <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`} title="No Attendance model in backend">N/A</td>
                                <td className={`px-4 py-2.5 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`} title="Not tracked by backend">N/A</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p className={`text-sm text-center py-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No department breakdown available for this college
                    </p>
                  )}

                  <p className={`text-[10px] mt-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                    * Attendance and Participation require an <code className="font-mono">Attendance</code> model in the backend. Batch-per-department breakdown requires an enhanced analytics endpoint.
                  </p>
                </>
              ) : (
                <div className={`text-center py-8 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                  <p className="text-sm font-medium">Failed to load analytics for this college</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── CollegeCards (main export) ───────────────────────────────────────────────
const CollegeCards = ({ colleges, isDark }) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return colleges;
    const q = search.toLowerCase();
    return colleges.filter(c =>
      (c.collegeName || c.institutionName || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q)
    );
  }, [colleges, search]);

  const handleExportAll = () => {
    if (!colleges.length) return;
    const rows = colleges.map(c => ({
      'College Name': c.collegeName || c.institutionName,
      'City': c.city || '—',
      'Status': c.status || 'active',
      'Total Students': c.totalStudents || 0,
      'Departments': c.departments?.length || c.totalDepartments || 0,
    }));
    exportToCSV(
      rows,
      Object.keys(rows[0]).map(k => ({ header: k, accessor: k })),
      'All_Colleges_Summary'
    );
  };

  return (
    <div className="space-y-4">
      {/* Search + Export */}
      <div className="flex items-center gap-3">
        <div className={`flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border ${isDark ? 'bg-[#0F225C] border-[#1a3884]/60' : 'bg-white border-gray-300'}`}>
          <Search size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search colleges by name or city…"
            className={`flex-1 text-sm bg-transparent focus:outline-none ${isDark ? 'text-gray-200 placeholder-gray-600' : 'text-gray-700 placeholder-gray-400'}`}
          />
        </div>
        <button
          onClick={handleExportAll}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex-shrink-0 ${
            isDark ? 'bg-[#1A3884]/30 text-blue-400 hover:bg-[#1A3884]/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
          }`}
        >
          <Download size={14} />
          Export All
        </button>
      </div>

      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        {filtered.length} {filtered.length === 1 ? 'college' : 'colleges'} — click any card to expand department breakdown
      </p>

      {/* Cards */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className={`text-center py-16 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No colleges found</p>
          </div>
        ) : (
          filtered.map((college, i) => (
            <CollegeCard key={college._id} college={college} isDark={isDark} index={i} />
          ))
        )}
      </div>
    </div>
  );
};

export default CollegeCards;
